import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  Search, Store, UserCircle, Plug, Mail, KeyRound, Building2, Target,
  Home, Filter, Handshake, Bot, Newspaper, CreditCard, HeartPulse, BarChart3, Bell, BookOpen,
  DollarSign, TrendingUp, Users, ChevronRight, Zap,
} from 'lucide-react';
import { authFetch } from '../../lib/auth';

/* Global Command Palette (Cmd+K) — Spotlight-style: modules, data, AI agents and actions.
   Closes with Esc, after navigating, or automatically on window/tab switch. */

const MODULES = [
  { to: '/admin/ai-os', icon: Home, en: 'Executive Overview', es: 'Resumen Ejecutivo', den: 'Daily brief, health and priorities', des: 'Brief diario, salud y prioridades' },
  { to: '/admin/ai-os/pipeline', icon: Filter, en: 'Revenue Pipeline', es: 'Revenue Pipeline', den: 'Leads, funnel and revenue in one flow', des: 'Leads, funnel e ingresos en un flujo' },
  { to: '/admin/ai-os/pipeline?tab=sales', icon: DollarSign, en: 'Sales Intelligence', es: 'Inteligencia de Ventas', den: 'Health scores and next actions per lead', des: 'Health scores y próximas acciones por lead' },
  { to: '/admin/ai-os/growth', icon: TrendingUp, en: 'Growth Center', es: 'Growth Center', den: 'Google, SEO, Ads, scores and forecasts', des: 'Google, SEO, Ads, scores y predicciones' },
  { to: '/admin/ai-os/pipeline?tab=customers', icon: Users, en: 'Customer Intelligence', es: 'Inteligencia de Clientes', den: 'Retention, audiences and lifecycle', des: 'Retención, audiencias y ciclo de vida' },
  { to: '/admin/ai-os/affiliates', icon: Handshake, en: 'Affiliates', es: 'Afiliados', den: 'Partners, clicks and commissions', des: 'Partners, clicks y comisiones' },
  { to: '/admin/ai-os/autonomy', icon: Bot, en: 'AI Executive Team', es: 'AI Executive Team', den: 'Executive brief, chat, tasks and live operations of your AI team', des: 'Executive brief, chat, tareas y operación en vivo de tu equipo AI' },
  { to: '/admin/ai-os/autonomy?tab=ops', icon: Bot, en: 'Live Operations', es: 'Operación en vivo', den: 'What every executive is doing right now', des: 'Qué está haciendo cada ejecutivo ahora mismo' },
  { to: '/admin/ai-os/restaurants', icon: Store, en: 'Organization', es: 'Organización', den: 'Restaurant OS instances and companies', des: 'Instancias de Restaurant OS y empresas' },
  { to: '/admin/ai-os/users', icon: UserCircle, en: 'Identity', es: 'Identidad', den: 'People, roles, access and audit', des: 'Personas, roles, accesos y auditoría' },
  { to: '/admin/ai-os/content', icon: Newspaper, en: 'Marketing', es: 'Marketing', den: 'Content, articles and distribution', des: 'Contenido, artículos y distribución' },
  { to: '/admin/ai-os/finance', icon: CreditCard, en: 'Finance', es: 'Finanzas', den: 'Revenue, payments and invoices', des: 'Ingresos, pagos y facturas' },
  { to: '/admin/ai-os/system-health', icon: HeartPulse, en: 'Operations Center', es: 'Centro de Operaciones', den: 'System health, logs and audit', des: 'Salud del sistema, logs y auditoría' },
  { to: '/admin/ai-os/reports', icon: BarChart3, en: 'Reports', es: 'Reportes', den: 'Exports and periodic reports', des: 'Exports y reportes periódicos' },
  { to: '/admin/ai-os/notifications', icon: Bell, en: 'Notifications', es: 'Notificaciones', den: 'Everything the OS observed', des: 'Todo lo que observó el OS' },
  { to: '/admin/ai-os/glossary', icon: BookOpen, en: 'Glossary', es: 'Glosario', den: 'What every metric means', des: 'Qué significa cada métrica' },
  { to: '/hub', icon: Home, en: 'Kitxens OS Home (Hub)', es: 'Home de Kitxens OS (Hub)', den: 'Command center across workspaces', des: 'Centro de mando entre workspaces' },
];

const AGENTS = [
  ['rachel', 'Rachel', 'AI Concierge'], ['eva', 'Eva', 'Executive Ops'], ['penny', 'Penny', 'Content & Newsletter'],
  ['sonny', 'Sonny', 'Social'], ['stan', 'Stan', 'Sales & Scans'], ['linda', 'Linda', 'Listings'],
];

const ACTIONS = [
  { to: '/admin/ai-os/pipeline?tab=sales', en: 'View hot leads', es: 'Ver leads calientes', den: 'Jump to leads with peak engagement', des: 'Salta a los leads con engagement al máximo' },
  { to: '/hub', en: 'Connect Google', es: 'Conectar Google', den: 'Search Console, GA4 and Ads', des: 'Search Console, GA4 y Ads' },
  { to: '/admin/editor', en: 'Write an article', es: 'Escribir un artículo', den: 'Open the Editorial Panel', des: 'Abre el Panel Editorial' },
  { to: '/demo/os', en: 'Launch client demo', es: 'Lanzar demo de cliente', den: 'Personalized demo environment', des: 'Entorno de demo personalizado' },
];

const GROUPS = [
  ['leads', Target, 'Leads (CRM)', 'Leads (CRM)'],
  ['restaurants', Store, 'Restaurants', 'Restaurantes'],
  ['identities', UserCircle, 'People', 'Personas'],
  ['affiliates', Handshake, 'Affiliates', 'Afiliados'],
  ['articles', Newspaper, 'Articles', 'Artículos'],
  ['companies', Building2, 'Companies', 'Empresas'],
  ['connections', Plug, 'Connection requests', 'Solicitudes de conexión'],
  ['invitations', Mail, 'Pending invitations', 'Invitaciones pendientes'],
  ['access_requests', KeyRound, 'Access requests', 'Solicitudes de acceso'],
];

function ResultCard({ icon: Icon, title, desc, active, onClick, onHover, testid }) {
  return (
    <button onClick={onClick} onMouseEnter={onHover} data-testid={testid}
      className={`w-full flex items-center gap-3 rounded-xl border px-3.5 py-2.5 text-left transition-all mb-1.5 ${active ? 'border-[#F77F11]/40 bg-orange-50 shadow-sm' : 'border-gray-100 bg-white hover:border-gray-200 hover:bg-gray-50'}`}>
      <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${active ? 'bg-[#F77F11]/15' : 'bg-gray-100'}`}>
        <Icon className={`w-4 h-4 ${active ? 'text-[#F77F11]' : 'text-gray-500'}`} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13.5px] font-semibold text-gray-900">{title}</span>
        {desc && <span className="block truncate text-[11.5px] text-gray-400">{desc}</span>}
      </span>
      <ChevronRight className={`w-4 h-4 shrink-0 ${active ? 'text-[#F77F11]' : 'text-gray-200'}`} />
    </button>
  );
}

export function GlobalSearch({ isEs }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [idx, setIdx] = useState(0);
  const navigate = useNavigate();
  const timer = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    const typing = () => {
      const el = document.activeElement;
      return el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
    };
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setOpen(true); return; }
      if (e.key === 'Escape') { setOpen(false); return; }
      if (e.metaKey || e.ctrlKey || e.altKey || typing()) return;
      if (e.key === '/') { e.preventDefault(); setOpen(true); }
      else if (e.key.toLowerCase() === 'h') { e.preventDefault(); setOpen(false); navigate('/hub'); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  useEffect(() => {
    if (!open) return undefined;
    const autoClose = () => { setOpen(false); setQ(''); };
    const onVis = () => { if (document.hidden) autoClose(); };
    window.addEventListener('blur', autoClose);
    document.addEventListener('visibilitychange', onVis);
    return () => { window.removeEventListener('blur', autoClose); document.removeEventListener('visibilitychange', onVis); };
  }, [open]);

  useEffect(() => {
    if (!open || q.trim().length < 2) { setRes(null); return undefined; }
    clearTimeout(timer.current);
    setBusy(true);
    timer.current = setTimeout(() => {
      authFetch(`/admin/restaurants/search/global?q=${encodeURIComponent(q.trim())}`)
        .then((r) => r.json()).then((j) => { setRes(j.groups || {}); setBusy(false); })
        .catch(() => setBusy(false));
    }, 300);
    return () => clearTimeout(timer.current);
  }, [q, open]);

  const s = q.trim().toLowerCase();
  const modules = useMemo(() => {
    const all = MODULES.filter((m) => !s || `${m.en} ${m.es} ${m.den} ${m.des}`.toLowerCase().includes(s));
    return s ? all.slice(0, 5) : all.slice(0, 6);
  }, [s]);
  const agents = useMemo(() => (s ? AGENTS.filter(([, n, r]) => `${n} ${r} ai agent`.toLowerCase().includes(s)).slice(0, 3) : []), [s]);
  const actions = useMemo(() => {
    const all = ACTIONS.filter((a) => !s || `${a.en} ${a.es} ${a.den} ${a.des}`.toLowerCase().includes(s));
    return s ? all.slice(0, 3) : all.slice(0, 4);
  }, [s]);

  const close = () => { setOpen(false); setQ(''); };
  const go = (to) => { close(); navigate(to); };
  const goItem = (group, item) => {
    close();
    if (group === 'leads') navigate(`/admin/ai-os/pipeline?tab=sales&person=${encodeURIComponent(item.email)}`);
    else if (group === 'identities' || group === 'managers' || group === 'invitations') navigate(`/admin/ai-os/users?q=${encodeURIComponent(item.email)}`);
    else if (group === 'affiliates') navigate('/admin/ai-os/affiliates');
    else if (group === 'articles') navigate('/admin/ai-os/content');
    else navigate('/admin/ai-os/restaurants');
  };

  const itemText = (group, item) => {
    if (group === 'leads') return [item.restaurant || item.owner || item.email, [item.owner, item.city].filter(Boolean).join(' · ') || item.email];
    if (group === 'companies') return [item.name, isEs ? 'Empresa' : 'Company'];
    if (group === 'restaurants') return [`${item.founding ? '⭐ ' : ''}${item.name}`, [item.city, item.company].filter(Boolean).join(' · ')];
    if (group === 'affiliates') return [item.name || item.email, `${isEs ? 'Afiliado' : 'Affiliate'} · ${item.status}`];
    if (group === 'articles') return [item.title, `${isEs ? 'Artículo' : 'Article'} · ${item.status}`];
    if (group === 'connections') return [`${item.restaurant_name} → ${item.provider}`, item.requested_by];
    if (group === 'access_requests') return [item.name || item.email, item.restaurant || item.email];
    return [item.name || item.email, `${item.workspace} · ${item.role}`];
  };

  const flat = useMemo(() => {
    const out = [];
    modules.forEach((m) => out.push({ kind: 'module', run: () => go(m.to) }));
    agents.forEach(([k]) => out.push({ kind: 'agent', run: () => go(`/admin/ai-os/workspace?agent=${k}`) }));
    actions.forEach((a) => out.push({ kind: 'action', run: () => go(a.to) }));
    if (res) GROUPS.forEach(([key]) => (res[key] || []).forEach((item) => out.push({ kind: key, run: () => goItem(key, item) })));
    return out;
  }, [modules, agents, actions, res]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setIdx(0); }, [q, open]);
  useEffect(() => {
    const el = listRef.current?.querySelector('[data-active="true"]');
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [idx]);

  const onInputKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setIdx((i) => Math.min(i + 1, flat.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setIdx((i) => Math.max(i - 1, 0)); }
    if (e.key === 'Enter' && flat[idx]) { e.preventDefault(); flat[idx].run(); }
  };

  const total = res ? Object.values(res).reduce((sm, arr) => sm + (arr?.length || 0), 0) : 0;
  let pos = -1;
  const next = () => { pos += 1; return pos; };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="aios-global-search-btn"
        title={isEs ? 'Buscar y comandos — ⌘K o /  ·  H = Hub' : 'Search & commands — ⌘K or /  ·  H = Hub'}
        className="hidden sm:inline-flex items-center gap-2 h-8 px-3 rounded-full border border-gray-200 bg-white text-[12px] text-gray-400 hover:text-gray-700 hover:border-gray-400 transition-colors">
        <Search className="w-3.5 h-3.5" /> {isEs ? 'Buscar' : 'Search'}
        <kbd className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-bold text-gray-400">⌘K</kbd>
        <kbd className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-bold text-gray-400">/</kbd>
      </button>
      {open && createPortal(
        <div className="fixed inset-0 z-[100] bg-neutral-950/70 backdrop-blur-[2px] flex items-start justify-center pt-[16vh] px-4" data-testid="aios-global-search-overlay"
          onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}
          style={{ animation: 'kxFadeIn .15s ease-out' }}>
          <div className="w-full max-w-3xl rounded-3xl bg-white/95 backdrop-blur-2xl overflow-hidden ring-1 ring-black/5"
            style={{ boxShadow: '0 40px 110px rgba(0,0,0,0.4), 0 10px 30px rgba(0,0,0,0.18)', animation: 'kxPaletteIn .18s cubic-bezier(0.16,1,0.3,1)' }}>
            <div className="flex items-center gap-2.5 px-5 pt-4 pb-2.5 border-b border-gray-100">
              <Search className="w-4 h-4 text-gray-400 shrink-0" />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onInputKey}
                data-testid="aios-global-search-input"
                placeholder={isEs ? 'Busca cualquier cosa: leads, restaurantes, personas, artículos, acciones…' : 'Search anything: leads, restaurants, people, articles, actions…'}
                className="flex-1 h-11 text-[14px] outline-none placeholder:text-gray-300 bg-transparent" />
              {busy ? <span className="text-[11px] text-gray-300">…</span>
                : <button onClick={close} className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-bold text-gray-400 hover:text-gray-600" data-testid="aios-search-close">ESC</button>}
            </div>
            <div className="max-h-[58vh] overflow-y-auto p-3" ref={listRef}>
              {modules.length > 0 && (
                <div className="mb-2" data-testid="aios-search-group-modules">
                  <p className="px-2 pt-1 pb-1.5 text-[10px] uppercase tracking-wider text-gray-400 font-bold">{isEs ? 'Módulos' : 'Modules'}</p>
                  {modules.map((m) => {
                    const p = next();
                    return <div key={m.to} data-active={p === idx}>
                      <ResultCard icon={m.icon} title={isEs ? m.es : m.en} desc={isEs ? m.des : m.den} active={p === idx}
                        onClick={() => go(m.to)} onHover={() => setIdx(p)}
                        testid={`aios-search-module-${m.to.split('/').pop() || 'overview'}`} /></div>;
                  })}
                </div>
              )}
              {agents.length > 0 && (
                <div className="mb-2" data-testid="aios-search-group-agents">
                  <p className="px-2 pt-1 pb-1.5 text-[10px] uppercase tracking-wider text-gray-400 font-bold">{isEs ? 'Agentes AI' : 'AI Agents'}</p>
                  {agents.map(([k, n, r]) => {
                    const p = next();
                    return <div key={k} data-active={p === idx}>
                      <ResultCard icon={Bot} title={n} desc={r} active={p === idx}
                        onClick={() => go(`/admin/ai-os/workspace?agent=${k}`)} onHover={() => setIdx(p)}
                        testid={`aios-search-agent-${k}`} /></div>;
                  })}
                </div>
              )}
              {actions.length > 0 && (
                <div className="mb-2" data-testid="aios-search-group-actions">
                  <p className="px-2 pt-1 pb-1.5 text-[10px] uppercase tracking-wider text-gray-400 font-bold">{isEs ? 'Acciones' : 'Actions'}</p>
                  {actions.map((a) => {
                    const p = next();
                    return <div key={a.to + a.en} data-active={p === idx}>
                      <ResultCard icon={Zap} title={isEs ? a.es : a.en} desc={isEs ? a.des : a.den} active={p === idx}
                        onClick={() => go(a.to)} onHover={() => setIdx(p)}
                        testid={`aios-search-action-${a.en.toLowerCase().replace(/\s+/g, '-')}`} /></div>;
                  })}
                </div>
              )}
              {res && total === 0 && !busy && (
                <p className="px-3 py-4 text-center text-[13px] text-gray-400" data-testid="aios-search-empty">{isEs ? 'Sin resultados en datos.' : 'No data results.'}</p>
              )}
              {res && GROUPS.map(([key, Icon, en, es]) => {
                const items = res[key] || [];
                if (!items.length) return null;
                return (
                  <div key={key} className="mb-2" data-testid={`aios-search-group-${key}`}>
                    <p className="px-2 pt-1 pb-1.5 text-[10px] uppercase tracking-wider text-gray-400 font-bold">{isEs ? es : en}</p>
                    {items.map((item, i) => {
                      const p = next();
                      const [title, desc] = itemText(key, item);
                      return <div key={i} data-active={p === idx}>
                        <ResultCard icon={Icon} title={title} desc={desc} active={p === idx}
                          onClick={() => goItem(key, item)} onHover={() => setIdx(p)}
                          testid={`aios-search-result-${key}-${i}`} /></div>;
                    })}
                  </div>
                );
              })}
              {!res && (
                <p className="px-3 py-3 text-center text-[11.5px] text-gray-300">
                  {isEs ? '↑↓ navegar · Enter abrir · Esc cerrar · escribe 2+ letras para buscar en todos tus datos' : '↑↓ navigate · Enter open · Esc close · type 2+ letters to search across all your data'}
                </p>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
